"use client";

import React from "react";
import { Button, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { SearchIcon } from "@/components/icons";

interface SearchChatButtonProps {
  onOpenSearch: () => void;
  isDisabled: boolean;
  isCompact?: boolean;
}

export const SearchChatButton: React.FC<SearchChatButtonProps> = ({
  onOpenSearch,
  isDisabled,
  isCompact = false,
}) => {
  const { t } = useTranslation();

  const buttonContent = (
    <Button
      aria-label={t("Search Chats")}
      className={
        isCompact
          ? "btn-brand-primary min-w-0 w-10 p-2"
          : "btn-brand-primary h-[36px] w-full text-sm"
      }
      data-id="search-chat-button"
      isDisabled={isDisabled}
      size="sm"
      onPress={onOpenSearch}
    >
      {isCompact ? (
        // No colour class when enabled, so the icon inherits the button's own
        // `--brand-on-primary` foreground. It used to force `text-brand-secondary`,
        // which `use-theme` maps to the tenant *surface* colour. That paired with
        // the old `--brand-tertiary` (accent) fill, and once `.btn-brand-primary`
        // moved onto `--brand-primary` it left LSEG's dark surface navy sitting on
        // LSEG Blue at about 2.15:1, below the 3:1 floor for a graphic.
        <SearchIcon
          className={isDisabled ? "text-default-400" : undefined}
          height={20}
          width={20}
        />
      ) : (
        <>
          <SearchIcon
            className={isDisabled ? "text-default-400" : "text-default-50"}
            height={20}
            width={20}
          />
          {t("Search Chats")}
        </>
      )}
    </Button>
  );

  // Wrap in Tooltip only for compact mode
  if (isCompact) {
    return (
      <Tooltip>
        <TooltipTrigger>{buttonContent}</TooltipTrigger>
        <TooltipContent placement="right">{t("Search Chats")}</TooltipContent>
      </Tooltip>
    );
  }

  return buttonContent;
};
